/**
 * Complementary Products - Auto-populate from Shopify Recommendations
 * Fetches product recommendations based on the first item in the cart
 */

class ComplementaryProducts {
  constructor() {
    this.containers = document.querySelectorAll(
      "[data-complementary-products]"
    );
    this.mobileContainers = document.querySelectorAll(
      "[data-complementary-products-mobile]"
    );

    this.listenersAttached = false;
    this.productsLocked = false;
    this.cartDrawerOpen = false;
    this.currentProducts = [];
    this.renderedHTML = { desktop: "", mobile: "" };

    if (this.containers.length > 0 || this.mobileContainers.length > 0) {
      this.init();
    }
  }

  async init() {
    if (!this.listenersAttached) {
      this.watchCartDrawer();

      document.addEventListener("cart:updated", () => {
        this.refreshContainers();
        setTimeout(() => {
          this.updateComplementaryProducts();
        }, 100);
      });
      document.addEventListener("dialog:cart:updated", () => {
        this.refreshContainers();
        setTimeout(() => {
          this.updateComplementaryProducts();
        }, 100);
      });

      this.listenersAttached = true;
    }

    await this.updateComplementaryProducts();
  }

  refreshContainers() {
    this.containers = document.querySelectorAll(
      "[data-complementary-products]"
    );
    this.mobileContainers = document.querySelectorAll(
      "[data-complementary-products-mobile]"
    );
  }

  watchCartDrawer() {
    const observer = new MutationObserver(() => {
      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer) {
        const isOpen =
          cartDrawer.hasAttribute("open") ||
          cartDrawer.classList.contains("is-open");

        if (isOpen && !this.cartDrawerOpen) {
          this.cartDrawerOpen = true;
        } else if (!isOpen && this.cartDrawerOpen) {
          this.cartDrawerOpen = false;
          this.productsLocked = false;
        }
      }
    });

    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer) {
      observer.observe(cartDrawer, {
        attributes: true,
        attributeFilter: ["open", "class"],
      });

      const isOpen =
        cartDrawer.hasAttribute("open") ||
        cartDrawer.classList.contains("is-open");
      if (isOpen) {
        this.cartDrawerOpen = true;
      }
    }

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  async updateComplementaryProducts() {
    try {
      if (this.productsLocked) {
        this.ensureComplementaryVisible();
        return;
      }

      const cartResponse = await fetch("/cart.js");
      const cart = await cartResponse.json();

      if (!cart.items || cart.items.length === 0) {
        this.clearComplementaryProducts();
        this.productsLocked = false;
        return;
      }

      const firstItem = cart.items[0];
      const productId = firstItem.product_id;
      const variantId = firstItem.variant_id;
      const productHandle = firstItem.handle;

      const variantData = await this.fetchVariantMetafields(
        productHandle,
        variantId
      );

      const materialColorMetafield =
        variantData?.metafields?.custom?.material_color ||
        variantData?.metafields?.["custom.material_color"] ||
        null;

      this.firstCartItem = {
        ...firstItem,
        variant_metafields: {
          "custom.material_color": materialColorMetafield,
        },
      };

      const recommendations = await this.fetchRecommendations(
        productId,
        cart.items
      );

      if (recommendations && recommendations.length > 0) {
        await this.renderProducts(recommendations);

        if (!this.productsLocked) {
          this.productsLocked = true;
        }
      } else {
        this.clearComplementaryProducts();
      }
    } catch (error) {
      // Silent error handling
    }
  }

  async fetchVariantMetafields(productHandle, variantId) {
    try {
      if (
        window.variantMetafieldsCache &&
        window.variantMetafieldsCache[variantId]
      ) {
        return {
          metafields: {
            custom: {
              material_color:
                window.variantMetafieldsCache[variantId].material_color,
            },
          },
        };
      }

      const url = `/products/${productHandle}?view=metafields&variant=${variantId}`;
      const response = await fetch(url);
      if (!response.ok) {
        return null;
      }

      const data = await response.json();

      if (window.variantMetafieldsCache && data.variant_id) {
        window.variantMetafieldsCache[data.variant_id] = {
          id: data.variant_id,
          material_color: data.metafields?.custom?.material_color || null,
        };
      }

      return data;
    } catch (error) {
      return null;
    }
  }

  async enrichProductsWithMetafields(products) {
    for (const product of products) {
      const needsMetafields = product.variants.some(
        (v) =>
          !window.variantMetafieldsCache || !window.variantMetafieldsCache[v.id]
      );

      if (needsMetafields) {
        try {
          const response = await fetch(`/products/${product.handle}.js`);
          if (response.ok) {
            const fullProduct = await response.json();

            if (fullProduct.variants) {
              for (const variant of fullProduct.variants) {
                let materialColor = null;

                if (variant.metafields) {
                  materialColor =
                    variant.metafields?.custom?.material_color?.value ||
                    variant.metafields?.custom?.material_color ||
                    variant.metafields?.["custom.material_color"]?.value ||
                    variant.metafields?.["custom.material_color"] ||
                    null;
                }

                if (!window.variantMetafieldsCache) {
                  window.variantMetafieldsCache = {};
                }
                window.variantMetafieldsCache[variant.id] = {
                  id: variant.id,
                  material_color: materialColor,
                };
              }
            }
          }
        } catch (error) {
          // Silent error handling
        }
      }
    }

    for (const product of products) {
      for (const variant of product.variants) {
        if (
          window.variantMetafieldsCache &&
          window.variantMetafieldsCache[variant.id]
        ) {
          if (!variant.metafields) {
            variant.metafields = {};
          }
          if (!variant.metafields.custom) {
            variant.metafields.custom = {};
          }
          variant.metafields.custom.material_color =
            window.variantMetafieldsCache[variant.id].material_color;
        } else {
          if (!variant.metafields) {
            variant.metafields = {};
          }
          if (!variant.metafields.custom) {
            variant.metafields.custom = {};
          }
          variant.metafields.custom.material_color = null;
        }
      }
    }
  }

  async fetchRecommendations(productId, cartItems) {
    try {
      const url = `/recommendations/products.json?product_id=${productId}&limit=10&intent=complementary`;
      const response = await fetch(url);

      if (!response.ok) {
        return [];
      }

      const data = await response.json();

      if (!data.products || data.products.length === 0) {
        return [];
      }

      const cartProductIds = cartItems.map((item) => item.product_id);

      const filteredProducts = data.products
        .filter(
          (product) => !cartProductIds.includes(product.id) && product.available
        )
        .slice(0, 5);

      await this.enrichProductsWithMetafields(filteredProducts);

      return filteredProducts;
    } catch (error) {
      return [];
    }
  }

  async renderProducts(products) {
    const productCards = await Promise.all(
      products.map((product) =>
        this.createProductCard(product, this.firstCartItem)
      )
    );

    const renderedHTML = productCards.join("");
    this.renderedHTML.desktop = renderedHTML;
    this.renderedHTML.mobile = renderedHTML;
    this.currentProducts = products;

    this.containers.forEach((container) => {
      const listElement = container.querySelector("[data-complementary-list]");
      if (listElement) {
        listElement.innerHTML = renderedHTML;

        const complementaryContainer = container.closest(
          "[data-complementary-container]"
        );
        if (complementaryContainer) {
          complementaryContainer.style.display = "";
        }

        const crossSellFallback = document.querySelector(
          "[data-crosssell-fallback]"
        );
        if (crossSellFallback) {
          crossSellFallback.style.display = "none";
        }
      }
    });

    this.mobileContainers.forEach((container) => {
      const listElement = container.querySelector(
        "[data-complementary-list-mobile]"
      );
      if (listElement) {
        listElement.innerHTML = renderedHTML;
        container.style.display = "";

        const crossSellFallbackMobile = document.querySelector(
          "[data-crosssell-fallback-mobile]"
        );
        if (crossSellFallbackMobile) {
          crossSellFallbackMobile.style.display = "none";
        }
      }
    });

    this.updateCarouselNavigation(products.length);
  }
  createProductCard(product, cartItem = null) {
    let selectedVariant = product.variants[0];

    if (cartItem && product.variants.length > 1) {
      const cartMaterialColor =
        cartItem.variant_metafields?.["custom.material_color"];

      if (cartMaterialColor) {
        const matchingVariant = product.variants.find((variant) => {
          const variantTitle = variant.title || "";
          return variantTitle
            .toLowerCase()
            .includes(cartMaterialColor.toLowerCase());
        });

        if (matchingVariant && matchingVariant.available) {
          selectedVariant = matchingVariant;
        } else {
          selectedVariant =
            product.variants.find((v) => v.available) || product.variants[0];
        }
      }
    }

    const variant = selectedVariant;

    const currentPath = window.location.pathname;
    const isEnglish =
      currentPath.startsWith("/en") || currentPath.startsWith("/en/");
    let productTitle = product.title;

    if (isEnglish && product.tags) {
      const enTag = product.tags.find((tag) => tag.startsWith("en__"));
      if (enTag) {
        productTitle = enTag.replace("en__", "");
      }
    }

    const formatPrice = (cents) => {
      const currency = window.Shopify?.currency?.active || "EUR";
      const amount = (typeof cents === "string" ? cents.replace(".", "") : cents) / 100;
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    };

    const price = formatPrice(variant.price);
    const compareAtPrice = variant.compare_at_price
      ? formatPrice(variant.compare_at_price)
      : null;

    let priceHtml = `<span class="price">${price}</span>`;
    if (compareAtPrice && variant.compare_at_price > variant.price) {
      priceHtml = `
        <span class="price price--on-sale">${price}</span>
        <span class="price price--compare">${compareAtPrice}</span>
      `;
    }

    // Use variant image if available, otherwise fall back to product featured image
    const variantImage = variant.featured_image?.src || variant.image;
    const imageUrl = variantImage
      ? variantImage.replace(/\.(jpg|jpeg|png|gif|webp)/i, "_300x.$1")
      : product.featured_image
      ? product.featured_image.replace(/\.(jpg|jpeg|png|gif|webp)/i, "_300x.$1")
      : "";

    return `
      <div class="horizontal-product-card">
        ${
          imageUrl
            ? `
          <a href="${product.url}" class="horizontal-product-card__figure" data-instant>
            <img 
              src="${imageUrl}" 
              alt="${productTitle}" 
              loading="lazy" 
              class="horizontal-product-card__image"
              width="40"
              height="40"
            />
          </a>
        `
            : ""
        }
        
        <div class="horizontal-product-card__info">
          <div class="v-stack gap-1 justify-items-start">
            <a href="${
              product.url
            }" class="product-title text-sm font-w-medium upcase" data-instant>${productTitle}</a>
            <div class="price-list">
              ${priceHtml}
            </div>
          </div>

          ${
            product.available && variant.available
              ? `
              <form action="/cart/add" method="post" is="product-form" data-product-id="${product.id}">
                <input type="hidden" name="id" value="${variant.id}">
                <input type="hidden" name="quantity" value="1">
                <button type="submit" class="@narrow:horizontal-product-card__button link">Add to cart</button>
                <button type="submit" class="@large:horizontal-product-card__button button button--outline button--subdued">
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 0V10M0 5H10" stroke="currentColor" stroke-width="1.5"/>
                  </svg>
                </button>
              </form>
          `
              : ""
          }
        </div>
      </div>
    `;
  }

  updateCarouselNavigation(count) {
    const carouselNavs = document.querySelectorAll(
      '[aria-controls^="complementary-products-carousel-auto"]'
    );

    carouselNavs.forEach((nav) => {
      const buttons = nav.querySelectorAll("button");
      buttons.forEach((button, index) => {
        if (index < count) {
          button.style.display = "";
        } else {
          button.style.display = "none";
        }
      });
    });
  }

  clearComplementaryProducts() {
    this.renderedHTML = { desktop: "", mobile: "" };
    this.currentProducts = [];

    this.containers.forEach((container) => {
      const listElement = container.querySelector("[data-complementary-list]");
      if (listElement) {
        listElement.innerHTML = "";
      }

      const parentContainer = container.closest(
        "[data-complementary-container]"
      );
      if (parentContainer) {
        parentContainer.style.display = "none";
      }
    });

    this.mobileContainers.forEach((container) => {
      const listElement = container.querySelector(
        "[data-complementary-list-mobile]"
      );
      if (listElement) {
        listElement.innerHTML = "";
      }

      container.style.display = "none";
    });

    this.showCrossSellFallback();
  }

  showCrossSellFallback() {
    const desktopCrossSell = document.querySelector(
      "[data-crosssell-fallback]"
    );
    if (desktopCrossSell) {
      desktopCrossSell.style.display = "";
    }

    const mobileCrossSell = document.querySelector(
      "[data-crosssell-fallback-mobile]"
    );
    if (mobileCrossSell) {
      mobileCrossSell.style.display = "";
    }
  }

  ensureComplementaryVisible() {
    this.containers.forEach((container) => {
      const listElement = container.querySelector("[data-complementary-list]");
      if (
        listElement &&
        listElement.innerHTML.trim() === "" &&
        this.renderedHTML.desktop
      ) {
        listElement.innerHTML = this.renderedHTML.desktop;
      }
    });

    this.mobileContainers.forEach((container) => {
      const listElement = container.querySelector(
        "[data-complementary-list-mobile]"
      );
      if (
        listElement &&
        listElement.innerHTML.trim() === "" &&
        this.renderedHTML.mobile
      ) {
        listElement.innerHTML = this.renderedHTML.mobile;
      }
    });

    const desktopContainer = document.querySelector(
      "[data-complementary-container]"
    );
    if (desktopContainer) {
      desktopContainer.style.display = "";
    }

    const desktopCrossSell = document.querySelector(
      "[data-crosssell-fallback]"
    );
    if (desktopCrossSell) {
      desktopCrossSell.style.display = "none";
    }

    this.mobileContainers.forEach((container) => {
      container.style.display = "";
    });

    const mobileCrossSell = document.querySelector(
      "[data-crosssell-fallback-mobile]"
    );
    if (mobileCrossSell) {
      mobileCrossSell.style.display = "none";
    }
  }
}

let complementaryProductsInstance = null;

function initComplementaryProducts() {
  if (!complementaryProductsInstance) {
    complementaryProductsInstance = new ComplementaryProducts();
  } else {
    complementaryProductsInstance.refreshContainers();
    complementaryProductsInstance.updateComplementaryProducts();
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initComplementaryProducts);
} else {
  initComplementaryProducts();
}

document.addEventListener("cart:refresh", initComplementaryProducts);

const observeCartDrawer = () => {
  const cartDrawer = document.querySelector("cart-drawer");

  if (cartDrawer) {
    const observer = new MutationObserver((mutations) => {
      const hasComplementaryContainers = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node.nodeType === 1 &&
            (node.querySelector?.("[data-complementary-products]") ||
              node.querySelector?.("[data-complementary-products-mobile]"))
        )
      );

      if (hasComplementaryContainers) {
        if (complementaryProductsInstance) {
          complementaryProductsInstance.refreshContainers();
          complementaryProductsInstance.updateComplementaryProducts();
        }
      }
    });

    observer.observe(cartDrawer, {
      childList: true,
      subtree: true,
    });
  } else {
    setTimeout(observeCartDrawer, 500);
  }
};

observeCartDrawer();
